import React, {useEffect, useRef, useState} from 'react';
import {Dropdown, Table, Row, Col} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {Tooltip} from '@/components/tooltips';
import Spinner from '@/components/ui/Spinner';
import {FormModal} from 'components/modals';
import {ModalAlert} from '@/components/alerts';
import axios from 'axios';
import {useStoreActions} from 'easy-peasy';
import {useDrag, useDrop, DropTargetMonitor} from 'react-dnd';
import RuleSettingsForm from './RuleSettingsForm';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import {buildURL} from '@/utils/urlBuilder';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import Form from 'react-bootstrap/Form';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import useData from '@/utils/useData';
import usePaginatedData from '@/utils/usePaginatedData';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import {FormattedDateTime} from '@/components/utils/FormattedDateTime';
import {hasPermission} from '@/utils/permissions/permissionDebug';

declare global {
    interface Window {
        URLS: Record<string, string>;
    }
}

interface RuleSettingsFormProps {
    ruleId: number;
    channelId: number;
    onDone: () => void;
}

const TypedRuleSettingsForm = RuleSettingsForm as React.FC<RuleSettingsFormProps>;

interface User {
    userDisplayName: string;
}

interface AdditionalFilter {
    value: string;
    label: string;
    type: string;
}

interface Rule {
    id: number;
    actionId?: number;
    title: string;
    createdBy: User;
    additionalFilters: AdditionalFilter[];
    queryString: string;
    allFields: Record<string, Record<string, string>[]>;
    lastUpdated: string;
    conditionKontinue: boolean;
    channelShortname?: string;
    channelId?: number;
    active: boolean;
}

interface Action {
    id: number;
    title: string;
}


interface RuleListViewProps {
    activeChannelId?: string;
}

interface DragItem {
    type: string;
    id: number;
    index: number;
}

interface StoreState {}

interface StoreActions {
    rules: {
        changePosition: (positions: [number, number]) => void;
        commitReorder: (channelId?: string) => void;
        deleteRule: (id: number) => void;
        loadRules: (rules: Rule[]) => void;
    };
    customizeFormFieldChoices: {
        loadCustomizeFormFieldChoices: (choices: any) => void;
    };
}

interface RuleListRowProps {
    rule: Rule;
    openSettingsModal: (id: number) => void;
    onDeleteRule: (id: number, title: string) => void;
    activeChannelId?: string;
    index: number;
    sortableUntil: number;
    isRearrangeMode: boolean;
}

const RuleListRow = React.memo<RuleListRowProps>((
    {
        rule,
        openSettingsModal,
        onDeleteRule,
        activeChannelId = undefined,
        index,
        sortableUntil,
        isRearrangeMode,
    },
) => {
    const dndType = 'rule-row';

    const {
        data: actionsData,
        loading,
    } = usePaginatedData(window.URLS.actionsResource, {
        storeResolver: 'actions',
        requestParams: {
            params: {
                channelId: activeChannelId ? parseInt(activeChannelId, 10) : undefined,
            },
        },
    });

    const moveRule = useStoreActions((actions: StoreActions) => actions.rules.changePosition);
    const commitReorder = useStoreActions((actions: StoreActions) => actions.rules.commitReorder);
    const deleteRule = useStoreActions((actions: StoreActions) => actions.rules.deleteRule);

    const ref = useRef<HTMLTableRowElement>(null);
    const isRuleInActiveChannel = (!activeChannelId && !rule.channelId) ||
        (!!activeChannelId && parseInt(activeChannelId, 10) === rule.channelId);
    const canMoveRule = isRearrangeMode && isRuleInActiveChannel;

    const [, drop] = useDrop<DragItem, void, any>({
        accept: dndType,
        hover(item: DragItem, monitor: DropTargetMonitor) {
            if (!isRearrangeMode) {
                return;
            }

            if (!ref.current) {
                return;
            }

            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();

            if (!clientOffset) {
                return;
            }

            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            if (hoverIndex >= sortableUntil) {
                return;
            }

            moveRule([dragIndex, hoverIndex]);
            item.index = hoverIndex;
        },
        drop() {
            if (!isRearrangeMode) {
                return;
            }
            commitReorder(activeChannelId);
        },
    });

    const [{isDragging}, drag, preview] = useDrag<DragItem, any, { isDragging: boolean }>({
        type: dndType,
        item: {
            type: dndType,
            id: rule.id,
            index,
        },
        canDrag: canMoveRule,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    preview(drop(ref));

    const deleteItself = (id: number, title: string) => {
        onDeleteRule(id, title);
    };

    return (
        <tr
            key={rule.id}
            ref={ref}
            style={{
                opacity: isDragging ? 0.3 : 1,
                backgroundColor: isDragging ? '#ffff66' : 'white',
            }}
        >
            <td className="index-icon">
                {
                    rule.conditionKontinue ? (
                        <Tooltip
                            content={i18n.t('tt_rule_continue_yes.text')}
                            placement="top"
                        >
                            <Icon name="mdi-arrow-down" className="text-success" />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            content={i18n.t('tt_rule_continue_no.text')}
                            placement="top"
                        >
                            <Icon name="mdi-subdirectory-arrow-right" className="text-danger" />
                        </Tooltip>
                    )
                }
            </td>
            <td>{rule.title || '-'}</td>
            <td>{actionsData.find((el: Action) => el.id === rule.actionId)?.title || '-'}</td>
            <td>{rule.createdBy.userDisplayName || '-'}</td>
            <td>{rule.channelShortname || '-'}</td>
            <td><FormattedDateTime dateTime={rule.lastUpdated} showTime={false} options={{setZone: false}} fallback="-" /></td>
            <td>
                {rule.active ? (<Icon name="mdi-check" className="text-success" />) : (
                    <Icon name="mdi-close" className="text-danger" />)}
            </td>
            <td className="text-right">
                {isRuleInActiveChannel && isRearrangeMode ? (
                    <div className="d-inline-block mr-1">
                        <Tooltip content={i18n.t('jt_move.text')} placement="top">
                            <Button
                                ref={drag}
                                variant="outline-info"
                                size="sm"
                                icon="mdi-swap-vertical"
                                data-cy="rule-list-null-swapvertical-btn"
                                style={{padding: '0.25rem', width: '33px', height: '35px'}}
                            />
                        </Tooltip>
                    </div>
                ) : null}

                <Tooltip content={i18n.t('jt_actions.text')} placement="top">
                    <Dropdown className="d-inline-block ml-2">
                        <Dropdown.Toggle
                            as={Button}
                            variant="outline-info"
                            size="sm"
                            icon="mdi-dots-horizontal"
                            className="dropdown-toggle-no-caret"
                            id="settings-dropdown-1"
                            data-cy="rule-settings-toggle"
                        />

                        <Dropdown.Menu align="end">
                            <Dropdown.Item
                                onClick={() => openSettingsModal(rule.id)}
                                data-cy="rule-settings-btn"
                            >
                                <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_settings.text')}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => deleteItself(rule.id, rule.title)}
                                data-cy="rule-settings-delete-btn"
                            >
                                <Icon name="mdi-delete" className="mr-2" /> {i18n.t('jt_delete.text')}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Tooltip>
            </td>
        </tr>
    );
});

RuleListRow.displayName = 'RuleListRow';

const RuleListView = React.memo<RuleListViewProps>((
    {
        activeChannelId = undefined,
    },
) => {
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<{ id: number, title: string } | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const setSearchQueryState = async (setSearchQuery: (value: string) => void, value: string) => {
        setSearchQuery(value);
    };
    const setSearchQueryStateDebounced = AwesomeDebouncePromise(setSearchQueryState, 350);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const {userData, loading: loadingUserData} = useOptimizedConfig();
    const [isRearrangeMode, setIsRearrangeMode] = useState<boolean>(false);

    const [settingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);
    const [activeRuleId, setActiveRuleId] = useState<number>(0);

    const deleteRule = useStoreActions((actions: StoreActions) => actions.rules.deleteRule);

    const storeCustomizeFormFieldChoices = useStoreActions(
        (actions: StoreActions) => actions.customizeFormFieldChoices.loadCustomizeFormFieldChoices,
    );

    const [rules, loadingRules] = useData<Rule[]>(window.URLS.rulesResource, {
        storeResolver: 'rules',
        actionResolver: 'loadRules',
        loadingResolver: 'loading',
        setLoadingResolver: 'setLoading',
        requestParams: {
            params: {
                channelId: activeChannelId ? parseInt(activeChannelId, 10) : undefined,
                query: searchQuery,
            },
        },
    });

    const {
        data: actions,
        loading: loadingActions,
    } = usePaginatedData(window.URLS.actionsResource, {
        storeResolver: 'actions',
        requestParams: {
            params: {
                channelId: activeChannelId ? parseInt(activeChannelId, 10) : undefined,
            },
        },
    });

    useEffect(() => {
        const fetchData = async () => {
            const getCustomizeFormFieldChoices = await axios.get(window.URLS.customizeFormFieldChoices);
            storeCustomizeFormFieldChoices(getCustomizeFormFieldChoices.data);
        };

        fetchData();
    }, [
        storeCustomizeFormFieldChoices,
    ]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('addrule')) {
            openSettingsModal(0);
        }
    }, []);

    const canCreateRules = hasPermission('fs.rule.create', userData?.permissions, true);

    const userCanAccessRulesResource = hasPermission('fs.rule.list', userData?.permissions, true);
    const userCanAccessActionsResource = hasPermission('fs.action.list', userData?.permissions, true);

    if ((!userCanAccessRulesResource && !loadingUserData) || (!userCanAccessActionsResource && !loadingUserData)) {
        return (
            <>
                <HelpTextBlock helpText={i18n.t('ht_for_every.text')} />

                <div className="alert alert-danger" role="alert">
                    {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                </div>
            </>
        );
    }

    const inputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQueryStateDebounced(setSearchQuery, event.target.value);
    };

    const openSettingsModal = (id: number) => {
        setActiveRuleId(id);
        setSettingsModalOpen(true);
    };

    const disableNewRules = loadingActions || actions.length === 0;

    const closeSettingsModal = () => {
        setActiveRuleId(0);
        setSettingsModalOpen(false);
    };

    const openDeleteConfirm = (id: number, title: string): void => {
        setItemToDelete({id, title});
        setDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = (): void => {
        setItemToDelete(null);
        setDeleteConfirmOpen(false);
        setIsDeleting(false);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!itemToDelete) {return;}

        setIsDeleting(true);
        try {
            const url = buildURL('ruleDetails', {rule_id: itemToDelete.id});
            await axios.delete(url);
            deleteRule(itemToDelete.id);
            closeDeleteConfirm();
        } catch (error) {
            setIsDeleting(false);
            throw error;
        }
    };

    const newRuleModal = () => {
        if (!disableNewRules) {
            setSettingsModalOpen(true);
        }
    };

    let firstNonSortableRuleIndex = rules.findIndex(
        (rule) => (activeChannelId || rule.channelId) && activeChannelId && parseInt(activeChannelId, 10) !== rule.channelId,
    );

    if (firstNonSortableRuleIndex < 0) {
        firstNonSortableRuleIndex = rules.length;
    }

    const generateTableContent = (children: React.ReactNode) => (
        <tr>
            <td colSpan={8}>
                {children}
            </td>
        </tr>
    );
    let tableContent: React.ReactNode;

    if (loadingRules || loadingActions) {
        tableContent = generateTableContent(
            <Spinner
                size="md"
                text={i18n.t('jt_loading_data_general.text')}
                centered
                minHeight="200px"
            />
        );
    } else if (rules && rules.length === 0) {
        tableContent = generateTableContent(
            <div className="alert alert-warning">
                {i18n.t('jt_rules_found.text')}
            </div>
        );
    } else if (rules && rules.length > 0) {
        tableContent = rules.map((rule, idx) => (
            <RuleListRow
                key={rule.id}
                rule={rule}
                index={idx}
                activeChannelId={activeChannelId}
                openSettingsModal={openSettingsModal}
                onDeleteRule={openDeleteConfirm}
                sortableUntil={firstNonSortableRuleIndex}
                isRearrangeMode={isRearrangeMode}
            />
        ));
    }

    return (
        <>
            <HelpTextBlock helpText={i18n.t('ht_for_every.text')} />
            <Row>
                <Col lg={3}>
                    <Form className="mb-2 d-flex align-items-center search-box" onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                    }}>
                        <Form.Control
                            type="text"
                            placeholder={i18n.t('es_search.text')}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => inputChange(event)}
                            data-cy="eval-search-field"
                        />
                    </Form>
                </Col>
                <Col lg={9} className="d-flex justify-content-end align-items-center">
                    <Tooltip
                        content={i18n.t(isRearrangeMode ? 'jt_exit_rearrange.text' : 'jt_rearrange_rules.text')}
                        placement="top"
                    >
                        <Button
                            variant={isRearrangeMode ? 'outline-danger' : 'outline-info'}
                            size="sm"
                            className="mb-2 mr-2"
                            onClick={() => setIsRearrangeMode((prev) => !prev)}
                            icon={isRearrangeMode ? 'mdi-close' : 'mdi-swap-vertical'}
                            data-cy="rearrange-rules-btn"
                        />
                    </Tooltip>
                    {
                        canCreateRules ? (
                            <Button
                                variant="info"
                                size="sm"
                                className='mb-2'
                                icon="mdi-plus"
                                onClick={newRuleModal}
                                disabled={disableNewRules}
                                style={loadingActions ? {backgroundColor: '#009efb', borderColor: '#009efb', color: '#fff'} : undefined}
                                data-cy="new-rule-btn"
                            >
                                {i18n.t('jt_add_new_rule.text')}
                            </Button>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text')}
                                placement="top"
                            >
                                <div style={{cursor: 'not-allowed', display: 'inline-block'}}>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon="mdi-plus"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                        data-cy="rule-list-plus-btn"
                                    >
                                        {i18n.t('jt_add_new_rule.text')}
                                    </Button>
                                </div>
                            </Tooltip>
                        )
                    }
                    {disableNewRules && !loadingActions && canCreateRules ? (
                        <Tooltip
                            content={i18n.t('tt_you_have.text')}
                            placement="bottom"
                        >
                            <span className="d-inline-block" style={{marginLeft: '-100%', width: '100%', height: '100%', position: 'absolute', top: 0, left: 0}}></span>
                        </Tooltip>
                    ) : null}
                </Col>
            </Row>

            {isRearrangeMode && (
                <div className="alert alert-info d-flex align-items-center mb-3">
                    <Icon name="mdi-information" className="mr-2" />
                    {i18n.t('jt_rearrange_rules_mode_active.text')}
                </div>
            )}


            <div className="card">
                <div className="card-body">
                    <div className="table-responsive">
                        <Table striped hover className="g-table" id="rule-list-table">
                            <thead>
                                <tr>
                                    <th />
                                    <th>{i18n.t('jt_name.text')}</th>
                                    <th>{i18n.t('jt_action.text')}</th>
                                    <th>{i18n.t('jt_created.text')}</th>
                                    <th>
                                        {i18n.t('jt_channel_shortname.text')}
                                        <Tooltip
                                            content={i18n.t('tt_the_rule.text')}
                                            placement="top"
                                        >
                                            <Icon name="mdi-information-outline" size="xs" className='text-info mx-2' />
                                        </Tooltip>
                                    </th>
                                    <th>{i18n.t('jt_last_modified.text')}</th>
                                    <th>{i18n.t('jt_active.text')}</th>
                                    <th className="text-right">{i18n.t('jt_action.text')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableContent}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>
            {
                settingsModalOpen ? (
                    <FormModal
                        isOpen={settingsModalOpen}
                        onClose={closeSettingsModal}
                        title={i18n.t('jt_rule_settings.text')}
                        size="xl"
                    >
                        {actions.length ? (
                            <TypedRuleSettingsForm
                                ruleId={activeRuleId}
                                channelId={activeChannelId ? parseInt(activeChannelId, 10) : 0}
                                onDone={closeSettingsModal}
                            />
                        ) : (
                            <div className="text-center" style={{padding: '1rem'}}>
                                <Spinner size="sm" text={i18n.t('sp_loading.text')} centered/>
                            </div>
                        )}
                    </FormModal>
                ) : null
            }
            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('jt_confirm_deletion.text')}
                message={itemToDelete ? i18n.t('es_are_you_sure_delete_rule.text', {title: itemToDelete.title}) : ''}
                actions={[
                    {
                        label: i18n.t('jt_delete.text'),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
});

RuleListView.displayName = 'RuleListView';

export default RuleListView;